import { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { APP_SETTINGS, LIGHT_MODE, LIGHT_MODE_LOCALSTORE, THEMES } from '../constants';

const initialSettings = {
  compact: true,
  direction: 'ltr',
  responsiveFontSizes: true,
  roundedCorners: true,
  theme: window.matchMedia('(prefers-color-scheme: dark)').matches
          ? THEMES.DARK
          : THEMES.DEFAULT,
  lightMode: LIGHT_MODE.AUTO
};

export const restoreSettings = () => {
  let settings = null;

  try {
    const storedData = window.sessionStorage.getItem(APP_SETTINGS);
    const storedLightMode = window.localStorage.getItem(LIGHT_MODE_LOCALSTORE);

    if (storedData) {
      settings = JSON.parse(storedData);
      if (storedLightMode) {
        settings.theme = resolveThemeFromLightMode(storedLightMode)
        settings.lightMode = storedLightMode
      }
    } else {
      settings = {
        compact: true,
        direction: 'ltr',
        responsiveFontSizes: true,
        roundedCorners: true,
        theme: window.matchMedia('(prefers-color-scheme: dark)').matches
          ? THEMES.DARK
          : THEMES.DEFAULT,
        lightMode: LIGHT_MODE.AUTO
      };
      if (storedLightMode) {
        settings.theme = resolveThemeFromLightMode(storedLightMode)
        settings.lightMode = storedLightMode
      }
      storeSettings(settings);
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

const resolveThemeFromLightMode = (storedLightMode) => {
  let theme
  switch (storedLightMode) {
    case LIGHT_MODE.AUTO:
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.DEFAULT
      break;
    case LIGHT_MODE.DARK:
      theme = THEMES.DARK
      break;
    case LIGHT_MODE.LIGHT:
      theme = THEMES.DEFAULT
      break;
    default:
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.DEFAULT
      break;
  }
  return theme
}

export const storeSettings = (settings) => {
  window.sessionStorage.setItem(APP_SETTINGS, JSON.stringify(settings));
  window.localStorage.setItem(LIGHT_MODE_LOCALSTORE, settings.lightMode);
};

const SettingsContext = createContext({
  settings: initialSettings,
  saveSettings: () => { }
});

export const SettingsProvider = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  useEffect(() => {
    if (settings.lightMode !== LIGHT_MODE.AUTO) return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      saveSettings({
        theme: resolveThemeFromLightMode(LIGHT_MODE.AUTO),
      });
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [settings.lightMode]);

  const saveSettings = (updatedSettings) => {
    setSettings(prev => {
      const mergedSettings = { ...prev, ...updatedSettings };
      storeSettings(mergedSettings);
      return mergedSettings;
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
